import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import RichTextEditor from '@/components/RichTextEditor';

interface TrainingSection {
  id: string;
  title: string;
  video_url: string | null;
  body: string;
  sort_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

const EMPTY_FORM = {
  title: '',
  video_url: '',
  body: '',
  is_published: true,
};

interface TrainingConfig {
  id: string;
  title: string;
  description: string;
  welcome_video_url: string | null;
  helpdesk_url: string | null;
  helpdesk_label: string | null;
}

const TrainingManagement: React.FC = () => {
  const [sections, setSections] = useState<TrainingSection[]>([]);
  const [config, setConfig] = useState<TrainingConfig | null>(null);
  const [configForm, setConfigForm] = useState({
    title: '',
    description: '',
    welcome_video_url: '',
    helpdesk_url: '',
    helpdesk_label: '',
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSection, setEditingSection] = useState<TrainingSection | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);

    const [sectionsRes, configRes] = await Promise.all([
      supabase
        .from('training_sections')
        .select('*')
        .order('sort_order', { ascending: true }),
      supabase.from('training_config').select('*').single(),
    ]);

    if (sectionsRes.error) {
      console.error('Failed to fetch training sections:', sectionsRes.error);
    } else {
      setSections(sectionsRes.data || []);
    }

    if (!configRes.error && configRes.data) {
      setConfig(configRes.data);
      setConfigForm({
        title: configRes.data.title,
        description: configRes.data.description,
        welcome_video_url: configRes.data.welcome_video_url || '',
        helpdesk_url: configRes.data.helpdesk_url || '',
        helpdesk_label: configRes.data.helpdesk_label || '',
      });
    }

    setLoading(false);
  };

  const fetchSections = async () => {
    const { data, error: fetchError } = await supabase
      .from('training_sections')
      .select('*')
      .order('sort_order', { ascending: true });

    if (fetchError) {
      console.error('Failed to fetch training sections:', fetchError);
    } else {
      setSections(data || []);
    }
  };

  const handleSaveConfig = async () => {
    if (!config) return;
    setSavingConfig(true);
    const { error: updateError } = await supabase
      .from('training_config')
      .update({
        title: configForm.title.trim() || 'Training Center',
        description: configForm.description,
        welcome_video_url: configForm.welcome_video_url.trim() || null,
        helpdesk_url: configForm.helpdesk_url.trim() || null,
        helpdesk_label: configForm.helpdesk_label.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', config.id);

    if (updateError) {
      alert('Failed to save settings: ' + updateError.message);
    } else {
      setConfig({
        ...config,
        ...configForm,
        welcome_video_url: configForm.welcome_video_url.trim() || null,
        helpdesk_url: configForm.helpdesk_url.trim() || null,
        helpdesk_label: configForm.helpdesk_label.trim() || null,
      });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2000);
    }
    setSavingConfig(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditingSection(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (section: TrainingSection) => {
    setEditingSection(section);
    setForm({
      title: section.title,
      video_url: section.video_url || '',
      body: section.body,
      is_published: section.is_published,
    });
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (editingSection) {
        const { error: updateError } = await supabase
          .from('training_sections')
          .update({
            title: form.title.trim(),
            video_url: form.video_url.trim() || null,
            body: form.body,
            is_published: form.is_published,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingSection.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('training_sections')
          .insert({
            title: form.title.trim(),
            video_url: form.video_url.trim() || null,
            body: form.body,
            sort_order: sections.length,
            is_published: form.is_published,
          });

        if (insertError) throw insertError;
      }

      setShowModal(false);
      fetchSections();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (section: TrainingSection) => {
    if (!confirm(`Delete "${section.title}"? This cannot be undone.`)) return;

    const { error: deleteError } = await supabase
      .from('training_sections')
      .delete()
      .eq('id', section.id);

    if (deleteError) {
      alert('Failed to delete: ' + deleteError.message);
    } else {
      fetchSections();
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sections.length) return;

    const current = sections[index];
    const target = sections[targetIndex];

    await supabase
      .from('training_sections')
      .update({ sort_order: target.sort_order, updated_at: new Date().toISOString() })
      .eq('id', current.id);

    await supabase
      .from('training_sections')
      .update({ sort_order: current.sort_order, updated_at: new Date().toISOString() })
      .eq('id', target.id);

    fetchSections();
  };

  return (
    <div className='max-w-5xl mx-auto'>
      {/* Page Settings */}
      {config && (
        <div className='bg-card border border-border rounded-lg p-5 mb-8'>
          <h2 className='text-lg font-semibold mb-4'>Page Settings</h2>
          <div className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Page Title</label>
                <input
                  type='text'
                  value={configForm.title}
                  onChange={(e) => setConfigForm({ ...configForm, title: e.target.value })}
                  className='w-full px-3 py-2 bg-background border border-input rounded text-sm focus:outline-none focus:border-primary'
                  placeholder='Training Center'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Description</label>
                <input
                  type='text'
                  value={configForm.description}
                  onChange={(e) => setConfigForm({ ...configForm, description: e.target.value })}
                  className='w-full px-3 py-2 bg-background border border-input rounded text-sm focus:outline-none focus:border-primary'
                  placeholder='A short description shown below the title'
                />
              </div>
            </div>
            <div>
              <label className='block text-sm text-muted-foreground mb-1'>Welcome Video URL</label>
              <input
                type='text'
                value={configForm.welcome_video_url}
                onChange={(e) => setConfigForm({ ...configForm, welcome_video_url: e.target.value })}
                className='w-full px-3 py-2 bg-background border border-input rounded text-sm focus:outline-none focus:border-primary'
                placeholder='https://youtube.com/watch?v=... (optional)'
              />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Help Desk URL</label>
                <input
                  type='text'
                  value={configForm.helpdesk_url}
                  onChange={(e) => setConfigForm({ ...configForm, helpdesk_url: e.target.value })}
                  className='w-full px-3 py-2 bg-background border border-input rounded text-sm focus:outline-none focus:border-primary'
                  placeholder='https://...'
                />
              </div>
              <div>
                <label className='block text-sm text-muted-foreground mb-1'>Help Desk Link Text</label>
                <input
                  type='text'
                  value={configForm.helpdesk_label}
                  onChange={(e) => setConfigForm({ ...configForm, helpdesk_label: e.target.value })}
                  className='w-full px-3 py-2 bg-background border border-input rounded text-sm focus:outline-none focus:border-primary'
                  placeholder='For additional help visit our help desk'
                />
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <button
                onClick={handleSaveConfig}
                disabled={savingConfig}
                className='px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50'
              >
                {savingConfig ? 'Saving...' : 'Save Settings'}
              </button>
              {configSaved && <span className='text-green-500 text-sm'>Saved</span>}
            </div>
          </div>
        </div>
      )}

      {/* Sections list */}
      <div className='flex items-center justify-between mb-4'>
        <h2 className='text-lg font-semibold'>Training Sections</h2>
        <button
          onClick={openCreate}
          className='px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium'
        >
          + Add Section
        </button>
      </div>

      {loading ? (
        <div className='text-muted-foreground text-center py-12'>Loading sections...</div>
      ) : sections.length === 0 ? (
        <div className='text-center py-12'>
          <p className='text-muted-foreground mb-4'>No training sections yet.</p>
          <button
            onClick={openCreate}
            className='px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm'
          >
            Create your first section
          </button>
        </div>
      ) : (
        <div className='space-y-2'>
          {sections.map((section, index) => (
            <div
              key={section.id}
              className='bg-card border border-border rounded-lg px-5 py-4 flex items-center gap-4'
            >
              <div className='flex flex-col gap-0.5'>
                <button
                  onClick={() => handleMove(index, 'up')}
                  disabled={index === 0}
                  className='p-1 text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed transition-colors'
                  title='Move up'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 15l7-7 7 7' />
                  </svg>
                </button>
                <button
                  onClick={() => handleMove(index, 'down')}
                  disabled={index === sections.length - 1}
                  className='p-1 text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-not-allowed transition-colors'
                  title='Move down'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                  </svg>
                </button>
              </div>

              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className='text-muted-foreground text-sm font-mono'>{index + 1}.</span>
                  <span className='font-medium truncate'>{section.title}</span>
                  {!section.is_published && (
                    <span className='px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400 text-xs rounded-full flex-shrink-0'>
                      Draft
                    </span>
                  )}
                </div>
                {section.video_url && (
                  <p className='text-muted-foreground text-xs mt-1 truncate'>{section.video_url}</p>
                )}
              </div>

              <div className='flex gap-2 flex-shrink-0'>
                <button
                  onClick={() => openEdit(section)}
                  className='px-3 py-1.5 bg-secondary text-secondary-foreground rounded text-xs hover:bg-secondary/80 transition-colors'
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(section)}
                  className='px-3 py-1.5 bg-secondary text-destructive rounded text-xs hover:bg-secondary/80 transition-colors'
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className='fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4'>
          <div className='bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <h2 className='text-xl font-bold mb-4'>
                {editingSection ? `Edit: ${editingSection.title}` : 'Add Training Section'}
              </h2>

              {error && (
                <div className='bg-destructive/10 border border-destructive/30 text-destructive px-4 py-2 rounded mb-4 text-sm'>
                  {error}
                </div>
              )}

              <div className='space-y-4'>
                <div>
                  <label className='block text-sm text-muted-foreground mb-1'>Title</label>
                  <input
                    type='text'
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className='w-full px-3 py-2 bg-background border border-input rounded text-sm focus:outline-none focus:border-primary'
                    placeholder='e.g. Getting Started'
                  />
                </div>

                <div>
                  <label className='block text-sm text-muted-foreground mb-1'>Video URL</label>
                  <input
                    type='text'
                    value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    className='w-full px-3 py-2 bg-background border border-input rounded text-sm focus:outline-none focus:border-primary'
                    placeholder='https://youtube.com/watch?v=... or https://vimeo.com/...'
                  />
                  <p className='text-muted-foreground text-xs mt-1'>
                    YouTube or Vimeo URL. Leave blank for text-only sections.
                  </p>
                </div>

                <div>
                  <label className='block text-sm text-muted-foreground mb-1'>Content</label>
                  <RichTextEditor
                    value={form.body}
                    onChange={(html) => setForm({ ...form, body: html })}
                    placeholder='Section content...'
                  />
                </div>

                <div className='flex items-center gap-3'>
                  <input
                    type='checkbox'
                    id='trainingPublished'
                    checked={form.is_published}
                    onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                    className='rounded'
                  />
                  <label htmlFor='trainingPublished' className='text-sm text-foreground'>
                    Published (visible to users)
                  </label>
                </div>
              </div>

              <div className='flex gap-3 mt-6'>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className='flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium disabled:opacity-50'
                >
                  {saving ? 'Saving...' : editingSection ? 'Update Section' : 'Add Section'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className='px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors text-sm'
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingManagement;
